<template>
	<MainContent class="py-4">
		<form class="w-full flex flex-col items-center gap-5 px-4 py-0">
			<Section title="CONVÊNIO">
				<FormGroup
					v-model.trim="selected.partnership_id"
					class="w-full"
					label="CONVÊNIO"
					input-type="select"
					:required="true"
					:options="
						partnerships.map(p => ({
							...p,
							label: p.nome.toUpperCase(),
							value: p.id,
						}))
					"
					:input-classes="[
						{
							'field-error': submitted && !selectedPartnership,
						},
					]"
					placeholder="SELECIONE UM CONVÊNIO..."
					:intercept-select="true"
					@select="SelectPartnership"
				/>
			</Section>
		</form>
	</MainContent>
</template>

<script>
	import { mapGetters, mapActions } from 'vuex'

	export default {
		name: 'Partnership',
		components: {},
		props: {
			modality_id: {
				type: [String, Number],
				default: null,
			},
			partnership: {
				type: Object,
				default: null,
			},
			allowed_partnership_ids: {
				type: Array,
				default: null,
			},
		},
		data() {
			return {
				sidebarState: false,
				partnerships: [],
				selected: {
					partnership_id: null,
					indicator: null,
				},
				submitted: false,
			}
		},
		computed: {
			...mapGetters('system', ['modules', 'modalities']),
			...mapGetters('auth', ['requester_selected_id', 'requester_selected_object']),
			selectedPartnership() {
				return this.partnerships.find(p => p.id === parseInt(this.selected.partnership_id))
			},
			isFormValid() {
				return !!this.selectedPartnership
			},
		},
		watch: {
			partnership: {
				handler(val) {
					this.selected.partnership_id = val?.id ? parseInt(val.id) : null
				},
				immediate: true,
			},
			'selected.partnership_id': {
				handler(val) {
					this.submitted = false
					this.$emit('partnership-change', val)
				},
				immediate: true,
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			Previous() {
				this.$emit('previous')
			},
			SelectPartnership(partnership_id) {
				this.selected.partnership_id = partnership_id
				this.Continue()
			},
			async GetPartnerships() {
				this.StartLoading()

				return window.api
					.call('post', '/get-partnerships', {
						organization_id: this.requester_selected_id,
						modality_id: this.modality_id,
						// light: true,
					})
					.then(({ data }) => {
						if (data.response) {
							this.partnerships = data.partnerships

							if (this.allowed_partnership_ids !== null) {
								this.partnerships = this.partnerships.filter(p =>
									this.allowed_partnership_ids.includes(p.id)
								)
							}

							if (this.partnerships.length === 1) {
								this.selected.partnership_id = this.partnerships[0].id
							}

							this.$emit('on-load-partnerships', this.partnerships)
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			async Continue() {
				this.submitted = true
				if (!this.isFormValid) {
					await this.$nextTick()
					const firstError = this.$el.querySelector('.field-error, .vs--error, .date-wrapper--error')
					if (firstError) {
						firstError.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						})
					}
					return
				}

				this.$emit('next', {
					partnership: this.selectedPartnership,
				})
			},
		},
		async mounted() {
			if (this.modality_id) await this.GetPartnerships()
		},
	}
</script>

<style lang="scss" scoped>
	.section-header {
		display: flex;
		align-items: center;
		gap: 1rem;
		color: var(--fontColor);
		font-weight: 800;
		letter-spacing: 0.1em;
		margin-bottom: 2.5rem;

		&::before,
		&::after {
			content: '';
			flex: 1;
			height: 1px;
			background: var(--hr);
		}
	}

	.field-label {
		display: flex;
		align-items: center;
		text-transform: uppercase;
		font-size: 0.875rem;
		font-weight: 700;
		color: #64748b;
		margin-bottom: 6px;
		white-space: nowrap;
		padding: 0;
	}

	.select-button {
		background-color: var(--backgroundColorActiveNotSelected);
		border: solid 1px var(--borderColorActiveNotSelected);
		color: var(--fontColorActiveNotSelected);
		text-transform: uppercase;

		&::placeholder {
			color: var(--fontColorActiveNotSelected);
			opacity: 1;
		}

		option {
			color: var(--fontColorOptionHover);
			background-color: var(--optionBackgroundColor);
			text-transform: uppercase;

			&:hover {
				color: var(--fontColorOptionHover);
				background-color: var(--optionBackgroundColor);
			}
		}

		&:disabled {
			background-color: var(--disabledBackgroundColor) !important;
			border-color: var(--disabledBorderColor) !important;
			color: var(--disabledColor) !important;
		}
	}

	.form-control {
		text-transform: uppercase;

		&:focus {
			background-color: var(--backgroundColorActiveNotSelected);
			border: solid 1px var(--borderColorActiveNotSelected);
			color: var(--fontColor);
		}

		&::placeholder {
			color: var(--fontColorActiveNotSelected);
			opacity: 1;
		}

		&:not(:placeholder-shown) {
			background-color: var(--backgroundColorActiveSelected);
			border: solid 1px var(--newColor);
			color: var(--fontColorActiveSelected);
		}

		&:disabled {
			background-color: var(--disabledBackgroundColor) !important;
			border-color: var(--disabledBorderColor) !important;
			color: var(--disabledColor) !important;
			cursor: not-allowed;

			&::placeholder {
				color: var(--disabledColor) !important;
			}
		}
	}

	::v-deep .v-select {
		--vs-controls-color: var(--fontColorActiveNotSelected);
		--vs-border-color: var(--borderColorActiveNotSelected);
		--vs-border-width: 1px;
		--vs-border-radius: 8px;

		--vs-line-height: 1.4;
		--vs-font-size: 0.875rem;

		--vs-bg: var(--backgroundColorActiveNotSelected);
		--vs-selected-color: var(--fontColorActiveNotSelected);

		--vs-search-input-color: var(--fontColorActiveNotSelected);
		--vs-search-input-bg: var(--backgroundColorActiveNotSelected);
		--vs-search-input-placeholder-color: var(--fontColorActiveNotSelected);

		--vs-dropdown-bg: var(--optionBackgroundColor);
		--vs-dropdown-color: var(--fontColorOption);
		--vs-dropdown-box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);

		--vs-dropdown-option-padding: 4px 16px;
		--vs-dropdown-option-color: var(--fontColorOption);
		--vs-dropdown-option--active-bg: var(--optionBackgroundColor);
		--vs-dropdown-option--active-color: var(--fontColor);

		--vs-state-disabled-bg: var(--disabledBackgroundColor);
		--vs-state-disabled-color: var(--disabledColor);
		--vs-state-disabled-controls-color: var(--disabledColor);
		--vs-state-disabled-cursor: not-allowed;

		.vs__dropdown-toggle {
			background-color: var(--gridBackgroundColor);
			border-color: var(--elementsBorderColor);
			height: 3.438rem;
			padding: 0.6rem 1.2rem;
			align-items: center;
		}

		.vs__selected-options {
			align-items: center;
			flex-wrap: nowrap;
		}

		.vs__selected {
			margin: 0;
			padding: 0;
			color: var(--fontColorActiveSelected);
		}

		&.vs--open .vs__selected {
			color: var(--fontColorActiveNotSelected);
		}

		.vs__search,
		.vs__search:focus {
			color: var(--fontColorActiveNotSelected);
			background-color: transparent;
			font-size: 0.875rem;
			margin: 0;
			padding: 0;
			line-height: 1.5;
		}

		.vs__dropdown-menu {
			background-color: var(--optionBackgroundColor);
			border-color: var(--borderColorActiveNotSelected);
		}

		.vs__dropdown-option {
			color: var(--fontColorOption);
			font-size: var(--vs-font-size);

			&.vs__dropdown-option--highlight {
				background-color: var(--optionBackgroundColor) !important;
				color: var(--fontColorOptionHover);
			}
		}

		.vs__actions .vs__clear svg {
			width: 14px;
			height: 14px;
		}
	}

	::v-deep .v-select:has(.vs__selected) .vs__dropdown-toggle {
		background-color: var(--backgroundColorActiveSelected) !important;
		border-color: var(--newColor) !important;
		font-size: var(--vs-font-size) !important;
	}

	::v-deep .v-select:has(.vs__selected) .vs__selected {
		color: var(--fontColorActiveSelected) !important;
	}

	::v-deep .v-select:has(.vs__selected) .vs__search,
	::v-deep .v-select:has(.vs__selected) .vs__search:focus {
		color: var(--fontColorActiveSelected) !important;
	}

	::v-deep .v-select.vs--disabled {
		.vs__dropdown-toggle {
			background-color: var(--disabledBackgroundColor) !important;
			border-color: var(--disabledBorderColor) !important;
		}

		.vs__selected {
			color: var(--disabledColor) !important;
		}

		.vs__search,
		.vs__search::placeholder {
			color: var(--disabledColor) !important;
		}

		.vs__open-indicator {
			fill: var(--disabledColor) !important;
			background-color: var(--disabledBackgroundColor) !important;
		}
	}

	::v-deep .flatpickr-calendar {
		font-size: 1rem !important;
		scale: 0.99 !important;
		transform-origin: top left !important;
	}

	.info-button {
		padding: 0;
		border: none;
		background-color: transparent;
		cursor: pointer;

		i {
			font-size: 1.25rem;
			margin-top: 0.3rem;
			color: var(--fontInfoActive);
		}

		&:hover {
			i {
				color: var(--fontColorHover);
			}
		}

		&:disabled {
			cursor: default;

			i {
				color: var(--fontInfoInactive);
			}
		}
	}

	.info-anchor {
		position: relative;
		display: inline-flex;
		align-items: center;
	}

	.info-inline-popup {
		position: absolute;
		z-index: 200;
		top: calc(100% + 4px);
		left: 0;
		background-color: var(--tippyBackgroundColor);
		border: 1px solid var(--tippyBorderColor);
		border-radius: 0.5rem;
		padding: 0.75rem;
		width: 380px;
		max-width: calc(100vw - 16px);
		box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
		white-space: normal;

		&.info-inline-popup--up {
			top: auto;
			bottom: calc(100% + 4px);
		}
	}

	.info-modal-list {
		margin: 0;
		padding: 0;
		list-style: none;
	}

	.info-modal-item {
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
		color: var(--fontColor);
	}

	.cid-autocomplete {
		position: relative;
	}

	.cid-dropdown {
		position: absolute;
		z-index: 100;
		top: 100%;
		left: 0;
		right: 0;
		margin: 0;
		padding: 0;
		list-style: none;
		background-color: var(--optionBackgroundColor);
		border: 1px solid var(--borderColorActiveNotSelected);
		border-top: none;
		border-radius: 0 0 4px 4px;
		max-height: 220px;
		overflow-y: auto;
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
	}

	.cid-option {
		padding: 6px 16px;
		color: var(--fontColorOption);
		cursor: pointer;
		font-size: 0.875rem;
		text-transform: uppercase;

		&:hover,
		&.cid-option--active {
			background-color: var(--optionBackgroundColor);
			color: var(--fontColorOptionHover);
		}
	}

	.btn-new-indicator {
		padding: 0.68rem 1rem;
		width: 2.25rem;
		height: 2.25rem;
	}

	.indicator-select-wrapper ::v-deep .vs__dropdown-toggle {
		border-top-right-radius: 0 !important;
		border-bottom-right-radius: 0 !important;
	}

	::v-deep .v-select.vs--error .vs__dropdown-toggle {
		border-color: #eab308;
	}

	::v-deep .v-select.vs--error.vs--open .vs__dropdown-toggle {
		border-color: var(--elementsBorderColor);
	}

	.date-wrapper--error ::v-deep .flatpickr-alt-input {
		border-color: #eab308;
	}

	.data-group {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 1rem;
	}

	.grid-layout input.form-control:focus {
		background-color: var(--backgroundColorActiveSelected);
	}
</style>
